import React from 'react';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    Grid,
    Paper,
    Avatar,
    Slider,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    type SelectChangeEvent,
    Dialog,
    Slide,
    DialogContent,
} from '@mui/material';
import {
    User,
    Hash,
    Zap,
    Edit3,
    Youtube,
    Instagram,
    MusicIcon,
    Link,
    Check,
    Heart,
    MessageCircle,
    Video,
    Users,
    Sparkles,
    TrendingUp,
    Star,
    DollarSign,
    ImageIcon,
    Camera,
    Play,
    Shield,
} from 'lucide-react';
import { TransitionProps } from '@mui/material/transitions';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { mapFormDataToInfluencerPayload } from './helper';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import { useSelector } from 'react-redux';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';
import { IEnableAudoLink } from '../../types/global/helper';
import { InfluencerProfile, IPriceCard } from '../../redux/buzzbridge/types';

export interface Category {
    name: string;
    icon: string;
    color: string;
}

export interface Platform {
    name: string;
    icon: any;
    gradient: string;
    bgColor: string;
    lightBg: string;
}

export interface ContentType {
    name: string;
    icon: any;
    color: string;
    gradient: string;
}

interface IManualData {
    userName: string;
    followers: string;
    totalLikes: string;
    averageComments: string;
    engagementRate: string;
    videoCount: string;
}

export interface PlatformData {
    isActive: boolean;
    entryType: 'automatic' | 'manual';
    isLinked: boolean;
    rateCard: IPriceCard;
    manualData: IManualData;
    linkedContent: InfluencerProfile;
}

export interface FormData {
    name: string;
    categories: string[];
    selectedPlatform: string;
    platforms: Record<string, PlatformData>;
}

const platformContentTypes: Record<string, ContentType[]> = {
    Instagram: [
        {
            name: 'Post',
            icon: ImageIcon,
            color: '#E4405F',
            gradient: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
        },
        {
            name: 'Story',
            icon: Camera,
            color: '#FF6B6B',
            gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
        },
        {
            name: 'Reel',
            icon: Play,
            color: '#4ECDC4',
            gradient: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
        },
        {
            name: 'IGTV',
            icon: Video,
            color: '#9B59B6',
            gradient: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
        },
    ],
    YouTube: [
        {
            name: 'Video',
            icon: Youtube,
            color: '#FF0000',
            gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
        },
        {
            name: 'Short',
            icon: Play,
            color: '#FF4444',
            gradient: 'linear-gradient(135deg, #FF4444 0%, #CC0000 100%)',
        },
    ],
    TikTok: [
        {
            name: 'Video',
            icon: MusicIcon,
            color: '#000000',
            gradient: 'linear-gradient(135deg, #000000 0%, #FF0050 50%, #00F2EA 100%)',
        },
    ],
};

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface IInfluencerSignupForm {
    open: boolean;
    onClose: VoidFunction;
    gradients: any;
    onSubmit?: (payload: any) => void;
}

const initialState = {
    name: '',
    categories: [],
    selectedPlatform: 'Instagram',
    platforms: {
        Instagram: {
            isActive: false,
            entryType: 'automatic',
            isLinked: false,
            rateCard: {
                Post: [50, 500],
                Story: [25, 200],
                Reel: [100, 800],
                IGTV: [150, 1000],
            },
            manualData: {
                userName: '',
                followers: '',
                totalLikes: '',
                averageComments: '',
                engagementRate: '',
                videoCount: '',
            },
        },
        YouTube: {
            isActive: false,
            entryType: 'automatic',
            isLinked: false,
            rateCard: {
                Video: [200, 2000],
                Short: [75, 600],
            },
            manualData: {
                userName: '',
                followers: '',
                totalLikes: '',
                averageComments: '',
                engagementRate: '',
                videoCount: '',
            },
        },
        TikTok: {
            isActive: false,
            entryType: 'automatic',
            isLinked: false,
            rateCard: {
                Video: [75, 600],
            },
            manualData: {
                userName: '',
                followers: '',
                totalLikes: '',
                averageComments: '',
                engagementRate: '',
                videoCount: '',
            },
        },
    },
};

const parseSocialNames = (platform: string) => {
    if (platform === 'Instagram') return 'meta';
    if (platform === 'Youtube') return 'youtube';
    if (platform === 'TikTok') return 'tiktok';
    return 'meta';
};
export const InfluencerSignupForm: React.FC<IInfluencerSignupForm> = ({
    open,
    onClose,
    gradients,
    onSubmit,
}): JSX.Element => {
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    /** Get the config here */
    const platformSupportsAutoEntry: IEnableAudoLink =
        buzzBridgeConfig && buzzBridgeConfig.enableAutoLink
            ? buzzBridgeConfig.enableAutoLink
            : {
                  Instagram: true,
                  YouTube: false,
                  TikTok: true,
              };

    const [formData, setFormData] = useState<any>(initialState);
    const [isConnecting, setIsConnecting] = useState<boolean | undefined>(false);
    const { themeStyle, device, darkModeEnabled: isDark } = useThemeContext();
    const currentTheme = theme[themeStyle];
    const categories: Category[] = [
        { name: 'Lifestyle', icon: '🌟', color: '#FF6B6B' },
        { name: 'Fashion', icon: '👗', color: '#4ECDC4' },
        { name: 'Beauty & Skincare', icon: '💄', color: '#FF8C94' },
        { name: 'Fitness & Health', icon: '💪', color: '#F7DC6F' },
        { name: 'Travel & Adventure', icon: '✈️', color: '#98D8C8' },
        { name: 'Food & Cooking', icon: '🍕', color: '#DDA0DD' },
        { name: 'Technology & Gadgets', icon: '💻', color: '#96CEB4' },
        { name: 'Gaming', icon: '🎮', color: '#FFEAA7' },
        { name: 'Parenting & Family', icon: '👨‍👩‍👧', color: '#FFB347' },
        { name: 'Education & Career', icon: '📚', color: '#BB8FCE' },
        { name: 'Art & Design', icon: '🎨', color: '#F1948A' },
        { name: 'Music & Performance', icon: '🎤', color: '#7FB3D5' },
        { name: 'Finance & Investing', icon: '💰', color: '#58D68D' },
        { name: 'Comedy & Entertainment', icon: '🎬', color: '#85C1E9' },
        { name: 'Automotive', icon: '🚗', color: '#B2BABB' },
        { name: 'Book Recommendations', icon: '📖', color: '#A569BD' },
        { name: 'Sustainable Living', icon: '🌱', color: '#2ECC71' },
        { name: 'Pet Influencers', icon: '🐶', color: '#F5B7B1' },
        { name: 'Spirituality & Mindfulness', icon: '🧘', color: '#AED6F1' },
        { name: 'Minimalism', icon: '⚪', color: '#D5DBDB' },
        { name: 'DIY Home Renovation', icon: '🛠️', color: '#DC7633' },
    ];

    const platforms: Platform[] = [
        {
            name: 'Instagram',
            icon: Instagram,
            gradient: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
            bgColor: isDark ? '#E4405F' : '#E4405F',
            lightBg: '#FCE4EC',
        },
        {
            name: 'YouTube',
            icon: Youtube,
            gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
            bgColor: isDark ? '#FF0000' : '#FF0000',
            lightBg: '#FFE5E5',
        },
        {
            name: 'TikTok',
            icon: MusicIcon,
            gradient: 'linear-gradient(135deg, #000000 0%, #FF0050 50%, #00F2EA 100%)',
            bgColor: isDark ? '#fff' : '#000000',
            lightBg: '#F0F0F0',
        },
    ];

    interface ManualField {
        field: string;
        label: string;
        icon: any;
        color: string;
        type?: string;
    }

    const manualEntryFields: Record<string, ManualField[]> = {
        Instagram: [
            { field: 'userName', label: 'Username', icon: User, color: '#667eea' },
            { field: 'followers', label: 'Total Followers', icon: Users, color: '#f093fb', type: 'number' },
            { field: 'totalLikes', label: 'Total Likes', icon: Heart, color: '#ff6b6b', type: 'number' },
            {
                field: 'averageComments',
                label: 'Average Comments',
                icon: MessageCircle,
                color: '#4ecdc4',
                type: 'number',
            },
            {
                field: 'engagementRate',
                label: 'Engagement Rate (%)',
                icon: TrendingUp,
                color: '#45b7d1',
                type: 'number',
            },
            { field: 'videoCount', label: 'Content Count', icon: Video, color: '#96ceb4', type: 'number' },
        ],
        YouTube: [
            { field: 'userName', label: 'Channel Name', icon: User, color: '#ff0000' },
            { field: 'followers', label: 'Subscriber Count', icon: Users, color: '#f093fb', type: 'number' },
            { field: 'totalLikes', label: 'Avg Likes per Video', icon: Heart, color: '#ff6b6b', type: 'number' },
            {
                field: 'averageComments',
                label: 'Avg Comments per Video',
                icon: MessageCircle,
                color: '#4ecdc4',
                type: 'number',
            },
            {
                field: 'engagementRate',
                label: 'Engagement Rate (%)',
                icon: TrendingUp,
                color: '#45b7d1',
                type: 'number',
            },
            { field: 'videoCount', label: 'Video Count', icon: Video, color: '#96ceb4', type: 'number' },
        ],
        TikTok: [
            { field: 'userName', label: 'Handle', icon: User, color: '#00f2ea' },
            { field: 'followers', label: 'Followers', icon: Users, color: '#f093fb', type: 'number' },
            { field: 'totalLikes', label: 'Total Likes', icon: Heart, color: '#ff6b6b', type: 'number' },
            { field: 'averageComments', label: 'Avg Comments', icon: MessageCircle, color: '#4ecdc4', type: 'number' },
            {
                field: 'engagementRate',
                label: 'Engagement Rate (%)',
                icon: TrendingUp,
                color: '#45b7d1',
                type: 'number',
            },
            { field: 'videoCount', label: 'Video Count', icon: Video, color: '#96ceb4', type: 'number' },
        ],
    };

    const handleNameChange = (value: string): void => {
        setFormData((prev: any) => ({ ...prev, name: value }));
    };

    const handleCategoryChange = (event: SelectChangeEvent<string[]>): void => {
        const value = event.target.value as string[];
        if (value.length <= 3) {
            setFormData((prev: any) => ({ ...prev, categories: value }));
        }
    };

    const handlePlatformChange = (_: React.MouseEvent<HTMLElement>, newPlatform: string | null): void => {
        if (newPlatform) {
            setFormData((prev: any) => ({ ...prev, selectedPlatform: newPlatform }));
        }
    };

    const handlePlatformToggle = (platformName: string): void => {
        setFormData((prev: any) => ({
            ...prev,
            platforms: {
                ...prev.platforms,
                [platformName]: {
                    ...prev.platforms[platformName],
                    isActive: !prev.platforms[platformName].isActive,
                },
            },
        }));
    };

    const handleEntryTypeChange = (platformName: string, entryType: 'automatic' | 'manual'): void => {
        setFormData((prev: any) => ({
            ...prev,
            platforms: {
                ...prev.platforms,
                [platformName]: {
                    ...prev.platforms[platformName],
                    entryType,
                },
            },
        }));
    };

    const handlePlatformLink = (
        success: boolean,
        platformName: string,
        response: InfluencerProfile,
        loading?: boolean,
    ): void => {
        setIsConnecting(loading);
        /** Handle linking here */
        if (success) {
            setFormData((prev: any) => ({
                ...prev,
                platforms: {
                    ...prev.platforms,
                    [platformName]: {
                        ...prev.platforms[platformName],
                        isLinked: !prev.platforms[platformName].isLinked,
                        linkedContent: response,
                    },
                },
            }));
        } else {
            /** Force The Manual with a message */
        }
    };

    const handleRateCardChange = (platformName: string, contentType: string, newValue: number[]): void => {
        setFormData((prev: any) => ({
            ...prev,
            platforms: {
                ...prev.platforms,
                [platformName]: {
                    ...prev.platforms[platformName],
                    rateCard: {
                        ...prev.platforms[platformName].rateCard,
                        [contentType]: [newValue[0], newValue[1]],
                    },
                },
            },
        }));
    };

    const handleManualDataChange = (platformName: string, field: string, value: string): void => {
        setFormData((prev: any) => ({
            ...prev,
            platforms: {
                ...prev.platforms,
                [platformName]: {
                    ...prev.platforms[platformName],
                    manualData: {
                        ...prev.platforms[platformName].manualData,
                        [field]: value,
                    },
                },
            },
        }));
    };

    const handleSubmit = (): void => {
        const payload = mapFormDataToInfluencerPayload(formData);
        onSubmit && onSubmit(payload);
        /** reset state */
        setFormData(initialState);
        onClose && onClose();
    };

    const formatPrice = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(value);
    };

    const currentPlatform = platforms.find(p => p.name === formData.selectedPlatform)!;
    const currentPlatformData = formData.platforms[formData.selectedPlatform];

    const isAtLeastOnePlatformValid = (): boolean => {
        return (Object.entries(formData.platforms) as [keyof typeof formData.platforms, PlatformData][]).some(
            ([platformName, data]) => {
                if (!data.isActive) return false;

                if (data.entryType === 'automatic') {
                    return data.isLinked;
                }

                if (data.entryType === 'manual') {
                    const requiredFields = manualEntryFields[platformName as string].map(field => field.field);
                    return requiredFields.every(field => data.manualData[field as keyof IManualData].trim() !== '');
                }

                return false;
            },
        );
    };

    return (
        <Dialog
            scroll="paper"
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullWidth
            fullScreen
            maxWidth={'lg'}
            PaperProps={{
                style: {
                    backgroundColor: currentTheme.form_background, // make this a color variable
                    width: '100vw',
                    maxWidth: device.isMobile ? '100vw' : '700px',
                    borderRadius: device.isMobile ? 0 : 20,
                    border: `0.5px solid ${currentTheme.darkGray60}`,
                    backdropFilter: 'blur(12px)',
                    maxHeight: device.isMobile ? '100dvh' : '90vh',
                    position: 'relative',
                },
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                backgroundColor: 'transparent',
                '& .MuiCardContent-root': {
                    p: {
                        xs: 1,
                        sm: 2,
                    },
                },
            }}>
            <CloseIcon
                style={{ position: 'absolute', top: 20, right: 20, zIndex: 99999, cursor: 'pointer' }}
                stroke={currentTheme.tag_white}
                onClick={onClose}
            />
            <DialogContent sx={{ p: 0 }}>
                <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    style={{ position: 'relative', zIndex: 1 }}>
                    <Card
                        sx={{
                            maxWidth: 800,
                            width: '100%',
                            borderRadius: 4,
                            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                            backdropFilter: 'blur(10px)',
                            background: currentTheme.secondaryBg,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}>
                        <CardContent sx={{ p: 5 }}>
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 0.6 }}>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <motion.div
                                        animate={{ rotate: [0, 5, -5, 0] }}
                                        transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, repeatDelay: 3 }}>
                                        <Avatar
                                            sx={{
                                                width: 80,
                                                height: 80,
                                                margin: '0 auto 16px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                fontSize: '2rem',
                                            }}>
                                            🚀
                                        </Avatar>
                                    </motion.div>

                                    <Typography
                                        variant="h3"
                                        component="h1"
                                        gutterBottom
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 20, sm: 40 },
                                            fontWeight: 800,
                                            background: isDark
                                                ? 'linear-gradient(135deg, #667eea 0%,rgb(162, 75, 75) 100%)'
                                                : 'linear-gradient(135deg, #7b4397 0%, #dc2430 100%)',
                                            backgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            textShadow: isDark ? 'none' : '0 1px 1px rgba(0,0,0,0.1)', // optional
                                        }}>
                                        BuzzBridge
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: currentTheme.tag_white,
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 16,
                                        }}>
                                        <Sparkles size={20} style={{ color: currentTheme.tag_white }} />
                                        Connect with brands and grow your influence
                                        <Sparkles size={20} style={{ color: currentTheme.tag_white }} />
                                    </Typography>
                                </Box>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4, duration: 0.6 }}>
                                <Grid container spacing={4}>
                                    {/* Name Field */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            value={formData.name}
                                            onChange={e => handleNameChange(e.target.value)}
                                            InputLabelProps={{
                                                sx: {
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: currentTheme.tag_white,
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                                            borderRadius: '50%',
                                                            p: 0.5,
                                                            mr: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}>
                                                        <User size={16} color="white" />
                                                    </Box>
                                                ),
                                            }}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].placeholderGray50,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#667eea',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#667eea',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: currentTheme.tag_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>

                                    {/* Categories Field */}
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel
                                                style={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: currentTheme.tag_white,
                                                }}>
                                                Categories of Influence (Select up to 3)
                                            </InputLabel>
                                            <Select
                                                multiple
                                                value={formData.categories}
                                                onChange={handleCategoryChange}
                                                input={
                                                    <OutlinedInput label="Categories of Influence (Select up to 3)" />
                                                }
                                                startAdornment={
                                                    <Box
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                                                            borderRadius: '50%',
                                                            p: 0.5,
                                                            mr: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}>
                                                        <Hash size={16} color="white" />
                                                    </Box>
                                                }
                                                renderValue={selected => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {(selected as string[]).map(value => {
                                                            const category = categories.find(
                                                                cat => cat.name === value,
                                                            )!;
                                                            return (
                                                                <Chip
                                                                    key={value}
                                                                    label={
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                            }}>
                                                                            <span>{category.icon}</span>
                                                                            {value}
                                                                        </Box>
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        background: `linear-gradient(135deg, ${category.color}20, ${category.color}10)`,
                                                                        border: `1px solid ${category.color}40`,
                                                                        color: category.color,
                                                                        fontWeight: 600,
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            bgcolor: currentTheme.secondaryBg,
                                                            color: currentTheme.tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        },
                                                    },
                                                }}
                                                sx={{
                                                    borderRadius: 3,
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#f093fb',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#f093fb',
                                                    },
                                                    '.MuiSvgIcon-root': {
                                                        color: theme[themeStyle].placeholderGray50, // Icon color white
                                                    },
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].placeholderGray50,
                                                    },
                                                    bgcolor: currentTheme.secondaryBg,
                                                    color: currentTheme.tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                {categories.map(category => (
                                                    <MenuItem
                                                        key={category.name}
                                                        value={category.name}
                                                        disabled={
                                                            formData.categories.length >= 3 &&
                                                            !formData.categories.includes(category.name)
                                                        }>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <span style={{ fontSize: '1.2rem' }}>{category.icon}</span>
                                                            <Typography fontFamily={FONT_ENUM.MAIN_MEDIUM}>
                                                                {category.name}
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Platform Selection */}
                                    <Grid item xs={12}>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.5 }}>
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    borderRadius: 3,
                                                    background: currentTheme.secondaryBg,
                                                    border: `0.9px solid ${currentTheme.placeholderGray50}`,
                                                }}>
                                                <Typography
                                                    variant="h6"
                                                    gutterBottom
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 3,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: currentTheme.tag_white,
                                                        fontSize: 16,
                                                    }}>
                                                    <Star size={18} style={{ color: currentTheme.tag_white }} />
                                                    Select Platform to Configure
                                                </Typography>

                                                <ToggleButtonGroup
                                                    value={formData.selectedPlatform}
                                                    exclusive
                                                    onChange={handlePlatformChange}
                                                    sx={{ display: 'flex', width: '100%', gap: 0.3 }}>
                                                    {platforms.map(platform => (
                                                        <ToggleButton
                                                            key={platform.name}
                                                            value={platform.name}
                                                            sx={{
                                                                borderRadius: 1.5,
                                                                width: '100%',
                                                                px: {
                                                                    xs: 1,
                                                                    sm: 3,
                                                                },
                                                                py: 1.5,
                                                                border: `1px solid ${platform.bgColor}40`,
                                                                background:
                                                                    formData.selectedPlatform === platform.name
                                                                        ? platform.gradient
                                                                        : currentTheme.deep_grey_lighter,
                                                                color:
                                                                    formData.selectedPlatform === platform.name
                                                                        ? 'white'
                                                                        : platform.bgColor,
                                                                fontWeight: 600,
                                                                textTransform: 'capitalize',
                                                                '&:hover': {
                                                                    background: platform.gradient,
                                                                    color: 'white',
                                                                },
                                                                '&.Mui-selected': {
                                                                    background: platform.gradient,
                                                                    color: 'white',
                                                                    '&:hover': {
                                                                        background: platform.gradient,
                                                                    },
                                                                },
                                                            }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <platform.icon size={20} />
                                                                {platform.name}
                                                            </Box>
                                                        </ToggleButton>
                                                    ))}
                                                </ToggleButtonGroup>
                                            </Paper>
                                        </motion.div>
                                    </Grid>

                                    {/* Platform Configuration */}
                                    <Grid item xs={12}>
                                        {!isAtLeastOnePlatformValid() && (
                                            <Alert
                                                severity="warning"
                                                sx={{ borderRadius: 2, mb: 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                                Please activate and fill in at least one platform manually or connect it
                                                automatically before submitting.
                                            </Alert>
                                        )}
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={formData.selectedPlatform}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}>
                                                <Paper
                                                    sx={{
                                                        p: 4,
                                                        borderRadius: 3,
                                                        background: currentTheme.secondaryBg, //`linear-gradient(135deg, ${currentPlatform.lightBg} 0%, rgba(255, 255, 255, 0.9) 100%)`
                                                        border: `1px solid ${currentPlatform.bgColor}`,
                                                    }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            mb: 3,
                                                        }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                color: currentTheme.tag_white,
                                                                fontSize: 16,
                                                            }}>
                                                            <Avatar
                                                                sx={{
                                                                    background: currentPlatform.gradient,
                                                                    width: 40,
                                                                    height: 40,
                                                                }}>
                                                                <currentPlatform.icon size={20} color="white" />
                                                            </Avatar>
                                                            {currentPlatform.name} Configuration
                                                        </Typography>

                                                        <motion.div
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}>
                                                            <Button
                                                                variant={
                                                                    currentPlatformData.isActive
                                                                        ? 'contained'
                                                                        : 'outlined'
                                                                }
                                                                onClick={() =>
                                                                    handlePlatformToggle(formData.selectedPlatform)
                                                                }
                                                                sx={{
                                                                    borderRadius: 1,
                                                                    background: currentPlatformData.isActive
                                                                        ? currentPlatform.gradient
                                                                        : 'transparent',
                                                                    borderColor: currentPlatform.bgColor,
                                                                    color: currentPlatformData.isActive
                                                                        ? 'white'
                                                                        : currentPlatform.bgColor,
                                                                    '&:hover': {
                                                                        background: currentPlatform.gradient,
                                                                        color: 'white',
                                                                    },
                                                                }}>
                                                                {currentPlatformData.isActive ? 'Active' : 'Activate'}
                                                            </Button>
                                                        </motion.div>
                                                    </Box>

                                                    <AnimatePresence>
                                                        {currentPlatformData.isActive && (
                                                            <motion.div
                                                                initial={{ opacity: 0, height: 0 }}
                                                                animate={{ opacity: 1, height: 'auto' }}
                                                                exit={{ opacity: 0, height: 0 }}
                                                                transition={{ duration: 0.3 }}>
                                                                {/* Rate Card */}
                                                                <Box sx={{ mb: 4 }}>
                                                                    <Typography
                                                                        variant="h6"
                                                                        gutterBottom
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 2,
                                                                            mb: 3,
                                                                            color: currentTheme.tag_white,
                                                                            fontSize: 16,
                                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                        }}>
                                                                        <Box
                                                                            sx={{
                                                                                background:
                                                                                    'linear-gradient(135deg, #FFD700, #FFA500)',
                                                                                borderRadius: '50%',
                                                                                p: 1,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                            }}>
                                                                            <DollarSign size={20} color="white" />
                                                                        </Box>
                                                                        {currentPlatform.name} Rate Card
                                                                    </Typography>

                                                                    <Grid container spacing={2}>
                                                                        {platformContentTypes[currentPlatform.name].map(
                                                                            (contentType, index) => (
                                                                                <Grid
                                                                                    item
                                                                                    xs={12}
                                                                                    sm={6}
                                                                                    key={contentType.name}>
                                                                                    <motion.div
                                                                                        initial={{ opacity: 0, x: -20 }}
                                                                                        animate={{ opacity: 1, x: 0 }}
                                                                                        transition={{
                                                                                            delay: index * 0.1,
                                                                                        }}>
                                                                                        <Paper
                                                                                            sx={{
                                                                                                p: 3,
                                                                                                borderRadius: 2,
                                                                                                background:
                                                                                                    currentTheme.deep_grey,
                                                                                                border: `1px solid ${contentType.color}20`,
                                                                                            }}>
                                                                                            <Box
                                                                                                sx={{
                                                                                                    display: 'flex',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                    gap: 2,
                                                                                                    mb: 2,
                                                                                                }}>
                                                                                                <Avatar
                                                                                                    sx={{
                                                                                                        background:
                                                                                                            contentType.gradient,
                                                                                                        width: 35,
                                                                                                        height: 35,
                                                                                                    }}>
                                                                                                    <contentType.icon
                                                                                                        size={16}
                                                                                                        color="white"
                                                                                                    />
                                                                                                </Avatar>
                                                                                                <Box sx={{ flex: 1 }}>
                                                                                                    <Typography
                                                                                                        variant="subtitle2"
                                                                                                        fontFamily={
                                                                                                            FONT_ENUM.MAIN_BOLD
                                                                                                        }
                                                                                                        color={
                                                                                                            currentTheme.tag_white
                                                                                                        }
                                                                                                        fontWeight="bold">
                                                                                                        {
                                                                                                            contentType.name
                                                                                                        }
                                                                                                    </Typography>
                                                                                                    <Typography
                                                                                                        variant="caption"
                                                                                                        fontFamily={
                                                                                                            FONT_ENUM.MAIN_MEDIUM
                                                                                                        }
                                                                                                        color={
                                                                                                            currentTheme.tag_white
                                                                                                        }>
                                                                                                        {formatPrice(
                                                                                                            currentPlatformData
                                                                                                                .rateCard[
                                                                                                                contentType
                                                                                                                    .name
                                                                                                            ][0],
                                                                                                        )}{' '}
                                                                                                        -{' '}
                                                                                                        {formatPrice(
                                                                                                            currentPlatformData
                                                                                                                .rateCard[
                                                                                                                contentType
                                                                                                                    .name
                                                                                                            ][1],
                                                                                                        )}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            </Box>

                                                                                            <Slider
                                                                                                value={
                                                                                                    currentPlatformData
                                                                                                        .rateCard[
                                                                                                        contentType.name
                                                                                                    ]
                                                                                                }
                                                                                                onChange={(
                                                                                                    _,
                                                                                                    newValue,
                                                                                                ) =>
                                                                                                    handleRateCardChange(
                                                                                                        formData.selectedPlatform,
                                                                                                        contentType.name,
                                                                                                        newValue as number[],
                                                                                                    )
                                                                                                }
                                                                                                valueLabelDisplay="auto"
                                                                                                valueLabelFormat={
                                                                                                    formatPrice
                                                                                                }
                                                                                                min={10}
                                                                                                max={5000}
                                                                                                step={25}
                                                                                                sx={{
                                                                                                    color: contentType.color,
                                                                                                    '& .MuiSlider-thumb':
                                                                                                        {
                                                                                                            background:
                                                                                                                contentType.gradient,
                                                                                                        },
                                                                                                    '& .MuiSlider-track':
                                                                                                        {
                                                                                                            background:
                                                                                                                contentType.gradient,
                                                                                                        },
                                                                                                }}
                                                                                            />
                                                                                        </Paper>
                                                                                    </motion.div>
                                                                                </Grid>
                                                                            ),
                                                                        )}
                                                                    </Grid>
                                                                </Box>

                                                                {/* Entry Type Selection */}
                                                                <Box sx={{ mb: 3 }}>
                                                                    <Typography
                                                                        variant="h6"
                                                                        gutterBottom
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 2,
                                                                            fontWeight: 600,
                                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                            color: currentTheme.tag_white,
                                                                            fontSize: 16,
                                                                        }}>
                                                                        <Box
                                                                            sx={{
                                                                                background:
                                                                                    'linear-gradient(135deg, #FFD700, #FFA500)',
                                                                                borderRadius: '50%',
                                                                                p: 1,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                            }}>
                                                                            <Zap
                                                                                size={16}
                                                                                style={{ color: '#f093fb' }}
                                                                            />
                                                                        </Box>
                                                                        How would you like to add your{' '}
                                                                        {currentPlatform.name} details?
                                                                    </Typography>

                                                                    <RadioGroup
                                                                        value={currentPlatformData.entryType}
                                                                        onChange={e =>
                                                                            handleEntryTypeChange(
                                                                                formData.selectedPlatform,
                                                                                e.target.value as
                                                                                    | 'automatic'
                                                                                    | 'manual',
                                                                            )
                                                                        }
                                                                        row
                                                                        sx={{ mt: 2 }}>
                                                                        <FormControlLabel
                                                                            value="automatic"
                                                                            control={
                                                                                <Radio
                                                                                    sx={{
                                                                                        color: currentPlatform.bgColor,
                                                                                    }}
                                                                                />
                                                                            }
                                                                            label={
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1,
                                                                                        color: currentTheme.tag_white,
                                                                                        fontFamily:
                                                                                            FONT_ENUM.MAIN_MEDIUM,
                                                                                    }}>
                                                                                    <TrendingUp
                                                                                        size={16}
                                                                                        style={{
                                                                                            color: currentPlatform.bgColor,
                                                                                        }}
                                                                                    />
                                                                                    Automatic (Link Account)
                                                                                </Box>
                                                                            }
                                                                        />
                                                                        <FormControlLabel
                                                                            value="manual"
                                                                            control={
                                                                                <Radio sx={{ color: '#f093fb' }} />
                                                                            }
                                                                            label={
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1,
                                                                                        color: currentTheme.tag_white,
                                                                                        fontFamily:
                                                                                            FONT_ENUM.MAIN_MEDIUM,
                                                                                    }}>
                                                                                    <Edit3
                                                                                        size={16}
                                                                                        style={{ color: '#f093fb' }}
                                                                                    />
                                                                                    Manual Entry
                                                                                </Box>
                                                                            }
                                                                        />
                                                                    </RadioGroup>

                                                                    {/* Verification Notice for Manual Entry */}
                                                                    <AnimatePresence>
                                                                        {currentPlatformData.entryType === 'manual' && (
                                                                            <motion.div
                                                                                initial={{
                                                                                    opacity: 0,
                                                                                    height: 0,
                                                                                    y: -10,
                                                                                }}
                                                                                animate={{
                                                                                    opacity: 1,
                                                                                    height: 'auto',
                                                                                    y: 0,
                                                                                }}
                                                                                exit={{ opacity: 0, height: 0, y: -10 }}
                                                                                transition={{ duration: 0.3 }}>
                                                                                <Alert
                                                                                    severity="info"
                                                                                    sx={{
                                                                                        mt: 2,
                                                                                        borderRadius: 2,
                                                                                        background:
                                                                                            'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(103, 58, 183, 0.1) 100%)',
                                                                                        border: '1px solid rgba(33, 150, 243, 0.2)',
                                                                                    }}
                                                                                    icon={
                                                                                        <Box
                                                                                            sx={{
                                                                                                background:
                                                                                                    'linear-gradient(135deg, #2196F3, #673AB7)',
                                                                                                borderRadius: '50%',
                                                                                                p: 0.5,
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                            }}>
                                                                                            <Shield color="white" />
                                                                                        </Box>
                                                                                    }>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color={currentTheme.tag_white}
                                                                                        fontFamily={
                                                                                            FONT_ENUM.MAIN_MEDIUM
                                                                                        }
                                                                                        fontWeight="500">
                                                                                        <strong>
                                                                                            Additional Verification
                                                                                            Required:
                                                                                        </strong>{' '}
                                                                                        Manual entries require
                                                                                        verification of your{' '}
                                                                                        {currentPlatform.name} metrics.
                                                                                        Review time: 2-3 business days.
                                                                                    </Typography>
                                                                                </Alert>
                                                                            </motion.div>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </Box>

                                                                {/* Entry Method Content */}
                                                                <AnimatePresence mode="wait">
                                                                    {currentPlatformData.entryType === 'automatic' ? (
                                                                        <motion.div
                                                                            key="automatic"
                                                                            initial={{ opacity: 0, y: 20 }}
                                                                            animate={{ opacity: 1, y: 0 }}
                                                                            exit={{ opacity: 0, y: -20 }}
                                                                            transition={{ duration: 0.3 }}>
                                                                            {!platformSupportsAutoEntry[
                                                                                formData.selectedPlatform as keyof IEnableAudoLink
                                                                            ] &&
                                                                                currentPlatformData.entryType ===
                                                                                    'automatic' && (
                                                                                    <Alert
                                                                                        severity="warning"
                                                                                        sx={{
                                                                                            mt: 2,
                                                                                            borderRadius: 2,
                                                                                            background:
                                                                                                'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 87, 34, 0.1))',
                                                                                            border: '1px solid rgba(255, 193, 7, 0.2)',
                                                                                        }}
                                                                                        icon={
                                                                                            <Box
                                                                                                sx={{
                                                                                                    background:
                                                                                                        'linear-gradient(135deg, #FFC107, #FF5722)',
                                                                                                    borderRadius: '50%',
                                                                                                    p: 0.5,
                                                                                                    height: 32,
                                                                                                    width: 32,
                                                                                                    display: 'flex',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                    justifyItems:
                                                                                                        'center',
                                                                                                }}>
                                                                                                <Shield color="white" />
                                                                                            </Box>
                                                                                        }>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            color={
                                                                                                currentTheme.tag_white
                                                                                            }
                                                                                            fontFamily={
                                                                                                FONT_ENUM.MAIN_MEDIUM
                                                                                            }
                                                                                            fontWeight="500">
                                                                                            <strong>
                                                                                                Auto Entry Not
                                                                                                Available:
                                                                                            </strong>{' '}
                                                                                            {formData.selectedPlatform}{' '}
                                                                                            does not currently support
                                                                                            automatic linking. Please
                                                                                            enter your data manually.
                                                                                        </Typography>
                                                                                    </Alert>
                                                                                )}

                                                                            {platformSupportsAutoEntry[
                                                                                formData.selectedPlatform as keyof IEnableAudoLink
                                                                            ] && (
                                                                                <Paper
                                                                                    elevation={
                                                                                        currentPlatformData.isLinked
                                                                                            ? 8
                                                                                            : 2
                                                                                    }
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'space-between',
                                                                                        p: 3,
                                                                                        borderRadius: 3,
                                                                                        background:
                                                                                            currentPlatformData.isLinked
                                                                                                ? `linear-gradient(135deg, ${currentPlatform.lightBg} 0%, rgba(255, 255, 255, 0.9) 100%)`
                                                                                                : 'white',
                                                                                        border: currentPlatformData.isLinked
                                                                                            ? `2px solid ${currentPlatform.bgColor}40`
                                                                                            : '2px solid #f0f0f0',
                                                                                    }}>
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: 3,
                                                                                        }}>
                                                                                        <Avatar
                                                                                            sx={{
                                                                                                background:
                                                                                                    currentPlatform.gradient,
                                                                                                width: 50,
                                                                                                height: 50,
                                                                                            }}>
                                                                                            <currentPlatform.icon
                                                                                                size={24}
                                                                                                color="white"
                                                                                            />
                                                                                        </Avatar>

                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="h6"
                                                                                                sx={{
                                                                                                    fontFamily:
                                                                                                        FONT_ENUM.MAIN_BOLD,
                                                                                                }}
                                                                                                fontWeight="bold">
                                                                                                {currentPlatform.name}{' '}
                                                                                                Account
                                                                                            </Typography>
                                                                                            <Typography
                                                                                                variant="body2"
                                                                                                color="text.secondary"
                                                                                                sx={{
                                                                                                    fontFamily:
                                                                                                        FONT_ENUM.MAIN_MEDIUM,
                                                                                                }}>
                                                                                                {currentPlatformData.isLinked
                                                                                                    ? 'Successfully connected'
                                                                                                    : 'Ready to connect'}
                                                                                            </Typography>
                                                                                        </Box>

                                                                                        {currentPlatformData.isLinked && (
                                                                                            <motion.div
                                                                                                initial={{
                                                                                                    scale: 0,
                                                                                                    rotate: -180,
                                                                                                }}
                                                                                                animate={{
                                                                                                    scale: 1,
                                                                                                    rotate: 0,
                                                                                                }}
                                                                                                transition={{
                                                                                                    type: 'spring',
                                                                                                    stiffness: 500,
                                                                                                    delay: 0.2,
                                                                                                }}>
                                                                                                <Avatar
                                                                                                    sx={{
                                                                                                        background:
                                                                                                            'linear-gradient(135deg, #4CAF50, #45a049)',
                                                                                                        width: 35,
                                                                                                        height: 35,
                                                                                                    }}>
                                                                                                    <Check
                                                                                                        size={18}
                                                                                                        color="white"
                                                                                                    />
                                                                                                </Avatar>
                                                                                            </motion.div>
                                                                                        )}
                                                                                    </Box>

                                                                                    <motion.div
                                                                                        whileHover={{ scale: 1.05 }}
                                                                                        whileTap={{ scale: 0.95 }}>
                                                                                        <SocialLogin
                                                                                            provider={parseSocialNames(
                                                                                                formData.selectedPlatform,
                                                                                            )}
                                                                                            onClick={() =>
                                                                                                setIsConnecting(true)
                                                                                            }
                                                                                            onAuthComplete={(
                                                                                                success,
                                                                                                data,
                                                                                            ) => {
                                                                                                handlePlatformLink(
                                                                                                    success,
                                                                                                    formData.selectedPlatform,
                                                                                                    data,
                                                                                                );
                                                                                            }}
                                                                                            customButton={
                                                                                                <Button
                                                                                                    variant={
                                                                                                        currentPlatformData.isLinked
                                                                                                            ? 'outlined'
                                                                                                            : 'contained'
                                                                                                    }
                                                                                                    size="large"
                                                                                                    disabled={
                                                                                                        isConnecting ||
                                                                                                        currentPlatformData.isLinked
                                                                                                    }
                                                                                                    startIcon={
                                                                                                        currentPlatformData.isLinked ? (
                                                                                                            <Check
                                                                                                                size={
                                                                                                                    18
                                                                                                                }
                                                                                                            />
                                                                                                        ) : (
                                                                                                            <Link
                                                                                                                size={
                                                                                                                    18
                                                                                                                }
                                                                                                            />
                                                                                                        )
                                                                                                    }
                                                                                                    sx={{
                                                                                                        borderRadius: 3,
                                                                                                        px: 3,
                                                                                                        py: 1.5,
                                                                                                        fontWeight: 600,
                                                                                                        background:
                                                                                                            currentPlatformData.isLinked
                                                                                                                ? 'transparent'
                                                                                                                : currentPlatform.gradient,
                                                                                                        borderColor:
                                                                                                            currentPlatformData.isLinked
                                                                                                                ? currentPlatform.bgColor
                                                                                                                : 'transparent',
                                                                                                        color: currentPlatformData.isLinked
                                                                                                            ? currentPlatform.bgColor
                                                                                                            : 'white',
                                                                                                        '&:hover': {
                                                                                                            background:
                                                                                                                currentPlatformData.isLinked
                                                                                                                    ? `${currentPlatform.bgColor}10`
                                                                                                                    : currentPlatform.gradient,
                                                                                                            transform:
                                                                                                                'translateY(-2px)',
                                                                                                            boxShadow: `0 8px 25px ${currentPlatform.bgColor}40`,
                                                                                                        },
                                                                                                    }}>
                                                                                                    {currentPlatformData.isLinked
                                                                                                        ? 'Connected'
                                                                                                        : isConnecting
                                                                                                        ? 'Connecting...'
                                                                                                        : 'Connect'}
                                                                                                </Button>
                                                                                            }
                                                                                        />
                                                                                    </motion.div>
                                                                                </Paper>
                                                                            )}
                                                                        </motion.div>
                                                                    ) : (
                                                                        <motion.div
                                                                            key="manual"
                                                                            initial={{ opacity: 0, y: 20 }}
                                                                            animate={{ opacity: 1, y: 0 }}
                                                                            exit={{ opacity: 0, y: -20 }}
                                                                            transition={{ duration: 0.3 }}>
                                                                            <Paper
                                                                                sx={{
                                                                                    p: 3,
                                                                                    borderRadius: 3,
                                                                                    background:
                                                                                        'linear-gradient(135deg, rgba(240, 147, 251, 0.05) 0%, rgba(245, 87, 108, 0.05) 100%)',
                                                                                    border: '1px solid rgba(240, 147, 251, 0.2)',
                                                                                }}>
                                                                                <Typography
                                                                                    variant="h6"
                                                                                    gutterBottom
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 2,
                                                                                        fontWeight: 600,
                                                                                        mb: 3,
                                                                                        fontFamily:
                                                                                            FONT_ENUM.MAIN_MEDIUM,
                                                                                        fontSize: 16,
                                                                                        color: currentTheme.tag_white,
                                                                                    }}>
                                                                                    <Box
                                                                                        sx={{
                                                                                            background:
                                                                                                'linear-gradient(135deg, #f093fb, #f5576c)',
                                                                                            borderRadius: '50%',
                                                                                            p: 1,
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                        }}>
                                                                                        <Edit3
                                                                                            size={20}
                                                                                            color="white"
                                                                                        />
                                                                                    </Box>
                                                                                    {currentPlatform.name} Manual Entry
                                                                                </Typography>

                                                                                <Grid container spacing={3}>
                                                                                    {manualEntryFields[
                                                                                        formData.selectedPlatform
                                                                                    ].map((item, index) => (
                                                                                        <Grid
                                                                                            item
                                                                                            xs={12}
                                                                                            sm={6}
                                                                                            key={item.field}>
                                                                                            <motion.div
                                                                                                initial={{
                                                                                                    opacity: 0,
                                                                                                    y: 20,
                                                                                                }}
                                                                                                animate={{
                                                                                                    opacity: 1,
                                                                                                    y: 0,
                                                                                                }}
                                                                                                transition={{
                                                                                                    delay: index * 0.1,
                                                                                                }}>
                                                                                                <TextField
                                                                                                    fullWidth
                                                                                                    label={item.label}
                                                                                                    InputLabelProps={{
                                                                                                        sx: {
                                                                                                            fontFamily:
                                                                                                                FONT_ENUM.MAIN_MEDIUM,
                                                                                                            color: currentTheme.tag_white,
                                                                                                        },
                                                                                                    }}
                                                                                                    type={
                                                                                                        item.type ||
                                                                                                        'text'
                                                                                                    }
                                                                                                    value={
                                                                                                        currentPlatformData
                                                                                                            .manualData[
                                                                                                            item.field as keyof typeof currentPlatformData.manualData
                                                                                                        ]
                                                                                                    }
                                                                                                    onChange={e =>
                                                                                                        handleManualDataChange(
                                                                                                            formData.selectedPlatform,
                                                                                                            item.field,
                                                                                                            e.target
                                                                                                                .value,
                                                                                                        )
                                                                                                    }
                                                                                                    InputProps={{
                                                                                                        startAdornment:
                                                                                                            (
                                                                                                                <Box
                                                                                                                    sx={{
                                                                                                                        background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                                                                                                                        borderRadius:
                                                                                                                            '50%',
                                                                                                                        p: 0.5,
                                                                                                                        mr: 1,
                                                                                                                        display:
                                                                                                                            'flex',
                                                                                                                        alignItems:
                                                                                                                            'center',
                                                                                                                    }}>
                                                                                                                    <item.icon
                                                                                                                        size={
                                                                                                                            16
                                                                                                                        }
                                                                                                                        color="white"
                                                                                                                    />
                                                                                                                </Box>
                                                                                                            ),
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        '& .MuiOutlinedInput-root':
                                                                                                            {
                                                                                                                borderRadius: 3,
                                                                                                                '&:hover fieldset':
                                                                                                                    {
                                                                                                                        borderColor:
                                                                                                                            item.color,
                                                                                                                    },
                                                                                                                '&.Mui-focused fieldset':
                                                                                                                    {
                                                                                                                        borderColor:
                                                                                                                            item.color,
                                                                                                                    },
                                                                                                            },
                                                                                                        '& fieldset': {
                                                                                                            borderColor:
                                                                                                                theme[
                                                                                                                    themeStyle
                                                                                                                ]
                                                                                                                    .placeholderGray50,
                                                                                                        },
                                                                                                        '& .MuiInputBase-input':
                                                                                                            {
                                                                                                                color: currentTheme.tag_white,
                                                                                                                fontFamily:
                                                                                                                    FONT_ENUM.MAIN_MEDIUM,
                                                                                                            },
                                                                                                    }}
                                                                                                />
                                                                                            </motion.div>
                                                                                        </Grid>
                                                                                    ))}
                                                                                </Grid>
                                                                            </Paper>
                                                                        </motion.div>
                                                                    )}
                                                                </AnimatePresence>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </Paper>
                                            </motion.div>
                                        </AnimatePresence>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                size="large"
                                                onClick={handleSubmit}
                                                disabled={
                                                    !formData.name ||
                                                    formData.categories.length === 0 ||
                                                    !isAtLeastOnePlatformValid()
                                                }
                                                sx={{
                                                    py: 2,
                                                    borderRadius: 3,
                                                    fontSize: '1.1rem',
                                                    fontWeight: 700,
                                                    background:
                                                        'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                                                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                                                    '&:hover': {
                                                        background:
                                                            'linear-gradient(135deg, #5a6fd8 0%, #6a4190 50%, #e085f0 100%)',
                                                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.6)',
                                                        transform: 'translateY(-2px)',
                                                    },
                                                    '&:disabled': {
                                                        background: '#ccc',
                                                        boxShadow: 'none',
                                                    },
                                                    textTransform: 'capitalize',
                                                }}>
                                                🚀 Submit Application
                                            </Button>
                                        </motion.div>
                                    </Grid>
                                </Grid>
                            </motion.div>
                        </CardContent>
                    </Card>
                </motion.div>
            </DialogContent>
        </Dialog>
    );
};
